import db from '../db/db'
import { AnalyticsEvents, DeskEventParams } from '../types/AnalyticsEvents'
import Database from '../types/Database'
import { DeskFormData } from '../types/ServiceTypes'

import { AnalyticsService } from './AnalyticsService'
import ErrorHandler from './ErrorHandler'

/**
 * Service for handling desk-related business logic
 * Extracted from CreateDesk component to improve separation of concerns
 *
 * Requirements: 7.5 - Implements consistent error logging
 */
export class DeskService {
  /**
   * Generate a unique ID for new desks and offices
   */
  private static generateId(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0
        const v = c === 'x' ? r : (r & 0x3) | 0x8
        return v.toString(16)
      }
    )
  }

  /**
   * Create a new desk
   */
  static async createDesk(
    deskData: DeskFormData,
    user: Database.User,
    offices: Database.Office[]
  ): Promise<{
    desk: Database.Desk
    office?: Database.Office
    isNewOffice: boolean
  }> {
    try {
      if (!user.uid || user.uid === '') {
        throw new Error('User authentication required')
      }

      const newDeskId = this.generateId()

      // Check if office already exists
      const existingOffice = offices.find(
        office => office.name === deskData.officeName
      )

      let targetOfficeId: string
      let newOffice: Database.Office | undefined
      let isNewOffice = false

      if (!existingOffice) {
        // Create new office
        const newOfficeId = this.generateId()
        newOffice = {
          name: deskData.officeName,
          oid: newOfficeId,
          uid: user.uid
        }

        ErrorHandler.logInfo(
          'Creating new office',
          {
            officeName: newOffice.name,
            officeId: newOfficeId,
            userId: user.uid
          },
          { component: 'DeskService', action: 'createDesk' }
        )

        await db.addItem('offices', newOffice)

        // Update user's office IDs
        const newOfficeIds = user.officeids?.slice() ?? []
        newOfficeIds.push(newOfficeId)

        if (user.id) {
          await db.updateItem('users', user.id, {
            officeids: newOfficeIds
          })
        }

        targetOfficeId = newOfficeId
        isNewOffice = true
      } else {
        targetOfficeId = existingOffice.oid!
      }

      // Create the desk
      const newDesk = {
        name: deskData.name,
        did: newDeskId,
        office: targetOfficeId,
        officeName: deskData.officeName,
        uid: user.uid,
        labels: deskData.labels,
        availableTo: deskData.availableTo
      }

      ErrorHandler.logInfo(
        'Creating desk',
        {
          deskName: newDesk.name,
          deskId: newDeskId,
          officeName: deskData.officeName,
          userId: user.uid
        },
        { component: 'DeskService', action: 'createDesk' }
      )

      await db.addItem('desks', newDesk)

      // Track desk creation event
      try {
        const deskEventParams: DeskEventParams = {
          office_id: targetOfficeId
        }
        AnalyticsService.trackEvent(
          AnalyticsEvents.DESK_CREATED,
          deskEventParams
        )
      } catch (analyticsError) {
        // Analytics errors should not break desk creation
        console.error('Failed to track desk creation event:', analyticsError)
      }

      return {
        desk: newDesk as Database.Desk,
        office: newOffice,
        isNewOffice
      }
    } catch (error) {
      ErrorHandler.logSystemError('Error creating desk', error, {
        component: 'DeskService',
        action: 'createDesk'
      })
      throw new Error(`Failed to create desk: ${error}`)
    }
  }

  /**
   * Update an existing desk
   */
  static async updateDesk(
    deskId: string,
    updates: Partial<DeskFormData>
  ): Promise<void> {
    try {
      if (!deskId) {
        throw new Error('Desk ID is required for update')
      }

      // Get the desk to retrieve office ID for analytics
      const desk = await db.getById('desks', deskId)
      if (!desk) {
        throw new Error('Desk not found')
      }

      const updateData: Partial<Database.Desk> = {}

      if (updates.name !== undefined) updateData.name = updates.name
      if (updates.labels !== undefined) updateData.labels = updates.labels
      if (updates.availableTo !== undefined)
        updateData.availableTo = updates.availableTo

      ErrorHandler.logInfo(
        'Updating desk',
        {
          deskId,
          updateData
        },
        { component: 'DeskService', action: 'updateDesk' }
      )

      await db.updateItem('desks', deskId, updateData)

      // Track desk update event
      try {
        const deskEventParams: DeskEventParams = {
          office_id: desk.office || ''
        }
        AnalyticsService.trackEvent(
          AnalyticsEvents.DESK_UPDATED,
          deskEventParams
        )
      } catch (analyticsError) {
        // Analytics errors should not break desk update
        console.error('Failed to track desk update event:', analyticsError)
      }
    } catch (error) {
      ErrorHandler.logSystemError('Error updating desk', error, {
        component: 'DeskService',
        action: 'updateDesk'
      })
      throw new Error(`Failed to update desk: ${error}`)
    }
  }

  /**
   * Delete a desk
   */
  static async deleteDesk(deskId: string): Promise<void> {
    try {
      if (!deskId) {
        throw new Error('Desk ID is required for deletion')
      }

      // Get the desk to retrieve office ID for analytics
      const desk = await db.getById('desks', deskId)
      if (!desk) {
        throw new Error('Desk not found')
      }

      ErrorHandler.logInfo(
        'Deleting desk',
        {
          deskId
        },
        { component: 'DeskService', action: 'deleteDesk' }
      )

      await db.deleteItem('desks', deskId)

      // Track desk deletion event
      try {
        const deskEventParams: DeskEventParams = {
          office_id: desk.office || ''
        }
        AnalyticsService.trackEvent(
          AnalyticsEvents.DESK_DELETED,
          deskEventParams
        )
      } catch (analyticsError) {
        // Analytics errors should not break desk deletion
        console.error('Failed to track desk deletion event:', analyticsError)
      }
    } catch (error) {
      ErrorHandler.logSystemError('Error deleting desk', error, {
        component: 'DeskService',
        action: 'deleteDesk'
      })
      throw new Error(`Failed to delete desk: ${error}`)
    }
  }

  /**
   * Get desks for a specific office
   */
  static async getDesksForOffice(officeId: string): Promise<Database.Desk[]> {
    try {
      if (!officeId) {
        return []
      }

      const desks = await db.query({
        collection: 'desks',
        where: ['office', '==', officeId]
      })

      return desks
    } catch (error) {
      ErrorHandler.logNetworkError('Error getting desks for office', error, {
        component: 'DeskService',
        action: 'getDesksForOffice'
      })
      throw new Error(`Failed to get desks for office: ${error}`)
    }
  }

  /**
   * Get desks created by a specific user
   */
  static async getUserDesks(userId: string): Promise<Database.Desk[]> {
    try {
      if (!userId) {
        return []
      }

      const desks = await db.query({
        collection: 'desks',
        where: ['uid', '==', userId]
      })

      return desks
    } catch (error) {
      ErrorHandler.logNetworkError('Error getting user desks', error, {
        component: 'DeskService',
        action: 'getUserDesks'
      })
      throw new Error(`Failed to get user desks: ${error}`)
    }
  }

  /**
   * Validate desk form data
   */
  static validateDeskData(deskData: DeskFormData): string[] {
    const errors: string[] = []

    if (!deskData.name || deskData.name.trim() === '') {
      errors.push('Desk name is required')
    }

    if (!deskData.officeName || deskData.officeName.trim() === '') {
      errors.push('Office name is required')
    }

    // Validate email format in availableTo field if provided
    if (deskData.availableTo && deskData.availableTo.trim() !== '') {
      const emails = deskData.availableTo.split(',').map(email => email.trim())
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

      for (const email of emails) {
        if (email && !emailRegex.test(email)) {
          errors.push(`Invalid email format: ${email}`)
        }
      }
    }

    return errors
  }
}

export default DeskService
